import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Cherry, Layers, CircleDot, Grid3X3, Bomb, TrendingUp, Dice5, Loader2 } from 'lucide-react';

import api from '@/app/lib/axios';
import { SokoCoinIcon } from '@/app/components/SvgIcons';
import PaginationWrapper from '@/app/components/Wrappers/PaginationWrapper';
import { formatDate } from '@/Utils/FormatDate';
import { LiveBet } from '@/types/liveBets';
import { UserStatistics } from '@/types/userStatistics';

/** Same game icons the live-bets feed uses, so a row reads the same everywhere. */
const GAMES: Record<string, { label: string; icon: typeof Cherry }> = {
    slots:     { label: 'Slots',     icon: Cherry     },
    blackjack: { label: 'Blackjack', icon: Layers     },
    roulette:  { label: 'Roulette',  icon: CircleDot  },
    keno:      { label: 'Keno',      icon: Grid3X3    },
    mines:     { label: 'Mines',     icon: Bomb       },
    crash:     { label: 'Crash',     icon: TrendingUp },
};

const GAME_FILTERS = ['all', ...Object.keys(GAMES)];
const RESULT_FILTERS: { id: 'all' | 'win' | 'loss'; label: string }[] = [
    { id: 'all',  label: 'All results' },
    { id: 'win',  label: 'Wins'        },
    { id: 'loss', label: 'Losses'      },
];

interface BetHistoryResponse {
    data: LiveBet[];
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
}

export function StatisticsTab() {
    const [game, setGame] = useState('all');
    const [result, setResult] = useState<'all' | 'win' | 'loss'>('all');
    const [page, setPage] = useState(1);

    const { data: stats, isLoading: statsLoading } = useQuery<UserStatistics>({
        queryKey: ['userStatistics'],
        queryFn: async () => (await api.get('profile/statistics')).data.data,
        staleTime: 60_000,
    });

    const { data: history, isLoading: historyLoading, isError } = useQuery<BetHistoryResponse>({
        queryKey: ['betHistory', game, result, page],
        queryFn: async () => (await api.get('profile/bet-history', {
            params: { game, result, page },
        })).data,
        placeholderData: keepPreviousData,
        staleTime: 5_000,
    });

    // Changing a filter re-pages from the start — page 4 of "all" rarely exists in "wins".
    const applyFilter = (fn: () => void) => { fn(); setPage(1); };

    const wagered = stats?.total_soko_wagered ?? 0;
    const profit = stats?.net_profit ?? 0;

    return (
        <div className="space-y-6">
            {/* ---------- Headline numbers ---------- */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile label="Wagered" loading={statsLoading} value={wagered.toLocaleString()} coin />
                <StatTile
                    label="Net profit"
                    loading={statsLoading}
                    value={`${profit >= 0 ? '+' : '−'}${Math.abs(profit).toLocaleString()}`}
                    tone={profit >= 0 ? 'good' : 'bad'}
                    coin
                />
                <StatTile label="Rounds played" loading={statsLoading} value={(stats?.total_rounds ?? 0).toLocaleString()} />
                <StatTile label="Win rate" loading={statsLoading} value={`${stats?.win_rate ?? 0}%`} />
                <StatTile label="Biggest win" loading={statsLoading} value={(stats?.biggest_win ?? 0).toLocaleString()} tone="good" coin />
                <StatTile label="Best multiplier" loading={statsLoading} value={`${(stats?.best_multiplier ?? 0).toFixed(2)}x`} tone="good" />
                <StatTile label="Wins" loading={statsLoading} value={(stats?.total_wins ?? 0).toLocaleString()} />
                <StatTile label="Losses" loading={statsLoading} value={(stats?.total_losses ?? 0).toLocaleString()} />
            </div>

            {/* ---------- Per-game breakdown ---------- */}
            {!!stats?.by_game?.length && (
                <section>
                    <SectionTitle>By game</SectionTitle>
                    <div className="rounded-xl bg-gray-800/50 divide-y divide-white/5 overflow-hidden">
                        {stats.by_game.map(row => {
                            const meta = GAMES[row.game] ?? { label: row.game, icon: Dice5 };
                            const GameIcon = meta.icon;
                            const share = wagered > 0 ? Math.round((row.wagered / wagered) * 100) : 0;

                            return (
                                <div key={row.game} className="px-3 py-2.5">
                                    <div className="flex items-center justify-between gap-3 text-sm">
                                        <div className="flex items-center gap-2 text-gray-200 min-w-0">
                                            <GameIcon className="w-4 h-4 shrink-0 text-gray-400" />
                                            <span className="truncate capitalize">{meta.label}</span>
                                            <span className="text-xs font-mono tabular-nums text-gray-400 shrink-0">{row.rounds}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs font-mono tabular-nums shrink-0">
                                            <span className="text-gray-400">{row.wagered.toLocaleString()}</span>
                                            <span className={row.profit >= 0 ? 'text-emerald-400' : 'text-red-400/80'}>
                                                {row.profit >= 0 ? '+' : '−'}{Math.abs(row.profit).toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                                        <div className="h-full rounded-full bg-emerald-500/60" style={{ width: `${share}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ---------- Bet history ---------- */}
            <section>
                <SectionTitle>Bet history</SectionTitle>

                <div className="flex flex-wrap gap-1.5 mb-3">
                    {GAME_FILTERS.map(id => (
                        <FilterChip key={id} active={game === id} onClick={() => applyFilter(() => setGame(id))}>
                            {id === 'all' ? 'All games' : GAMES[id].label}
                        </FilterChip>
                    ))}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                    {RESULT_FILTERS.map(({ id, label }) => (
                        <FilterChip key={id} active={result === id} onClick={() => applyFilter(() => setResult(id))}>
                            {label}
                        </FilterChip>
                    ))}
                </div>

                <div className="rounded-xl bg-gray-800/50 overflow-hidden">
                    {historyLoading && !history ? (
                        <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-xs">
                            <Loader2 size={16} className="animate-spin" /> Loading bets…
                        </div>
                    ) : isError ? (
                        <div className="py-10 text-center text-xs text-red-400">Failed to load bet history.</div>
                    ) : !history?.data.length ? (
                        <div className="py-10 text-center text-xs text-gray-400">No bets match this filter.</div>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {history.data.map(bet => <BetRow key={bet.id} bet={bet} />)}
                        </div>
                    )}
                </div>

                {!!history && (
                    <div className="mt-4">
                        <PaginationWrapper
                            isLoading={historyLoading}
                            currentPage={history.current_page}
                            lastPage={history.last_page}
                            onPageChange={setPage}
                        />
                    </div>
                )}
            </section>
        </div>
    );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
    return (
        <h3 className="text-sm font-bold uppercase tracking-wide text-green-400 mb-3">
            {children}
        </h3>
    );
}

function StatTile({ label, value, loading, tone, coin }: {
    label: string;
    value: string;
    loading?: boolean;
    tone?: 'good' | 'bad';
    coin?: boolean;
}) {
    const toneClass = tone === 'good' ? 'text-emerald-400' : tone === 'bad' ? 'text-red-400' : 'text-white';

    return (
        <div className="rounded-xl bg-gray-800/50 px-3 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
            {loading ? (
                <div className="mt-1.5 h-5 w-16 rounded bg-white/5 animate-pulse" />
            ) : (
                <div className={`mt-1 flex items-center gap-1.5 font-mono font-bold text-lg tabular-nums ${toneClass}`}>
                    <span className="truncate">{value}</span>
                    {coin && <SokoCoinIcon size={14} />}
                </div>
            )}
        </div>
    );
}

function FilterChip({ active, onClick, children }: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                active
                    ? 'bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
        >
            {children}
        </button>
    );
}

function BetRow({ bet }: { bet: LiveBet }) {
    const meta = GAMES[bet.game] ?? { label: bet.game, icon: Dice5 };
    const GameIcon = meta.icon;

    return (
        <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_4rem_5.5rem] items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors">
            <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm text-gray-200">
                    <GameIcon className="w-4 h-4 shrink-0 text-gray-400" />
                    <span className="truncate">{meta.label}</span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">{formatDate(bet.created_at)}</div>
            </div>
            <div className="text-right text-xs font-mono text-gray-300 tabular-nums">
                {bet.bet.toLocaleString()}
            </div>
            <div className={`text-right text-xs font-mono tabular-nums ${bet.won ? 'text-emerald-400' : 'text-gray-500'}`}>
                {bet.multiplier.toFixed(2)}x
            </div>
            <div className={`text-right text-xs font-mono font-bold tabular-nums ${bet.won ? 'text-emerald-400' : 'text-red-400/70'}`}>
                {bet.won ? `+${(bet.payout - bet.bet).toLocaleString()}` : `−${bet.bet.toLocaleString()}`}
            </div>
        </div>
    );
}
